import { VgEmploymentState } from '@interfaces';
import {
  AccountTypes,
  IndividualDesignationListedTypes,
  InvestmentObjectives,
  YesNoEmpty,
} from '@interfaces/models';
import { AccountTriageState } from '@interfaces/state';
import { createSelector } from '@ngrx/store';
import {
  getDecedentInfoState,
  getFeatureToggleState,
  getIsReviewPageReachedAndEditMode,
  getIsReviewPageReachedAndSkipInvestmentRevision,
  getPersonalDataState,
  getTriageState,
  TransactionState,
} from '@store/reducers';
import { FeatureToggleState } from '@store/reducers/feature/feature.reducer';
import { PersonalDataState } from '@store/reducers/personal-data/personal-data.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import {
  selectDecedentInfoState,
  selectDecedentInheritanceWaiverState,
} from '@store/selectors/decedent-info/decedent-info.selectors';
import {
  selectInheritanceNewTermsToggle,
  selectInheritanceVCDToggle,
  selectL8ItwUploadToggle,
} from '@store/selectors/feature/feature.selectors';
import { selectIsEditingProfile } from '@store/selectors/open-account/open-account.selectors';
import { selectEmploymentMfeDataByOwnerIndex } from '@store/selectors/open-account/open-account.selectors';
import { selectMfePersonalAddressIsContinueConfirmed } from '@store/selectors/personal-info-address/personal-info-address.selectors';
import { nullState } from '@store/selectors/router/common/router.selectors';
import { selectIsL8ITWUploadPageVisited } from '@store/selectors/tax-document-upload/tax-document-upload.selectors';
import {
  selectAllTransactions,
  selectCurrentTransaction,
  selectCurrentTransactionHasOnlyExecutor,
  selectCurrentTransactionHasOnlyTrustee,
  selectCurrentTransactionId,
  selectCurrentTransactionInvestmentObjectives,
  selectCurrentTransactionType,
  selectIndividualExecutorEstateTransactions,
  selectIraExecutorEstateTransactions,
  selectIsCurrentTransactionFirstNewAccount,
  selectIsCurrentTransactionNewAccountTransaction,
  selectIsMultipleNewAccountTransactionExist,
  selectIsOAReviewPageReachedAndEditMode,
} from '@store/selectors/transaction-state/transaction-state.selectors';

import {
  getMultiInheritanceAccountTriageNextPage,
  getMultiInheritanceContactInfoNextPage,
  getMultiInheritanceDecedentInfoRoute,
  getMultiInheritanceEmploymentNextPage,
  getMultiInheritanceEstateNextPage,
  getMultiInheritanceIncomeNetWorthNextPage,
  getMultiInheritanceInvestObjNextPage,
  getMultiInheritanceL8ItwUploadNextPage,
  getMultiInheritanceLetterUploadNexPage,
  getMultiInheritanceNewAccountReviewNextPage,
  getMultiInheritancePersonalInfoNextPage,
  getMultiInheritanceReviewNextPage,
  getMultiInheritanceSettlementFundNextPage,
  getMultiInheritanceTermsNextPage,
  getMultiInheritanceTriageNextPage,
  getMultiInheritanceTrustInfoNextPage,
  getMultiInheritanceTrustTaxInfoNextPage,
  getMultiInheritanceTrustTriageNextPage,
  getMultiInheritanceUploadNextPage,
  isPAElligible as isPAElligibleFn,
} from './multi-inheritance-router.helpers';

export const selectMultiInheritanceTriageNextPage = createSelector(
  getTriageState,
  getFeatureToggleState,
  (triageState: TriageState, featureToggleState: FeatureToggleState) =>
    getMultiInheritanceTriageNextPage(triageState, featureToggleState),
);

export const selectMultiInheritanceTrustTriageNextPage = createSelector(
  getTriageState,
  getFeatureToggleState,
  (triageState: TriageState, featureToggleState: FeatureToggleState) =>
    getMultiInheritanceTrustTriageNextPage(triageState, featureToggleState),
);

export const selectMultiInheritanceDecedentInfoNextPage = createSelector(
  getDecedentInfoState,
  getFeatureToggleState,
  getIsReviewPageReachedAndEditMode,
  (decedentInfoState, featureToggleState: FeatureToggleState, isReviewPageReachedAndEditMode) =>
    getMultiInheritanceDecedentInfoRoute(
      decedentInfoState,
      featureToggleState,
      isReviewPageReachedAndEditMode,
    ),
);

export const selectMultiInheritanceUploadNextPage = createSelector(
  getIsReviewPageReachedAndEditMode,
  selectDecedentInheritanceWaiverState,
  selectL8ItwUploadToggle,
  (isReviewPageReachedAndEditMode, inheritanceWaiverState, selectL8ItwUploadToggle) =>
    getMultiInheritanceUploadNextPage(
      isReviewPageReachedAndEditMode,
      inheritanceWaiverState,
      selectL8ItwUploadToggle,
    ),
);

export const selectMultiInheritanceAccountTriageNextPage = createSelector(
  selectIsCurrentTransactionNewAccountTransaction,
  selectIsMultipleNewAccountTransactionExist,
  getIsReviewPageReachedAndEditMode,
  selectIsCurrentTransactionFirstNewAccount,
  selectCurrentTransactionInvestmentObjectives,
  selectInheritanceVCDToggle,
  selectCurrentTransaction,
  selectCurrentTransactionHasOnlyTrustee,
  selectCurrentTransactionHasOnlyExecutor,
  selectDecedentInfoState,
  selectL8ItwUploadToggle,
  selectIndividualExecutorEstateTransactions,
  selectIraExecutorEstateTransactions,
  selectIsL8ITWUploadPageVisited,
  selectMfePersonalAddressIsContinueConfirmed,
  (
    isNewAccount,
    isMultipleNewAccountTransaction,
    isReviewPageReachedAndEditMode,
    isCurrentTransactionFirstNewAccount,
    currentTransactionInvestmentObjectives: InvestmentObjectives | null,
    isVCDToggleEnabled,
    currentTransaction,
    isOnlyTrustee,
    isOnlyExecutor,
    decedentInfoState,
    l8ItwUploadToggle,
    individualExecutorEstateTransactions,
    iraExecutorEstateTransactions,
    isL8ITWUploadPageVisited,
    isContactInfoConfirmed,
  ) => {
    const accountTriageState = currentTransaction?.accountTriage || ({} as AccountTriageState);
    const isIndividualExecutor =
      accountTriageState.accountTypeInheriting === AccountTypes.INDV &&
      accountTriageState.individualDesignation === IndividualDesignationListedTypes.EXECUTOR;
    const isIRASingleExecutor =
      accountTriageState.accountTypeInheriting === AccountTypes.IRA && isOnlyExecutor;
    const isPAElligible = currentTransaction ? isPAElligibleFn(currentTransaction) : false;
    const isExecSpouseFlow =
      currentTransaction?.accountTriage?.isDecedentSpouse === YesNoEmpty.YES && isOnlyExecutor;

    const currentId = currentTransaction?.currentTransactionId;
    const otherIndividualExecutorEstateTransactions = individualExecutorEstateTransactions.filter(
      (transaction) => transaction.currentTransactionId !== currentId,
    );
    const otherIraExecutorEstateTransactions = iraExecutorEstateTransactions.filter(
      (transaction) => transaction.currentTransactionId !== currentId,
    );

    return getMultiInheritanceAccountTriageNextPage(
      isNewAccount,
      isMultipleNewAccountTransaction,
      isReviewPageReachedAndEditMode,
      isCurrentTransactionFirstNewAccount ?? false,
      currentTransactionInvestmentObjectives,
      isVCDToggleEnabled,
      isIndividualExecutor,
      isOnlyExecutor,
      isOnlyTrustee,
      isIRASingleExecutor,
      decedentInfoState.inheritanceWaiverState,
      isPAElligible,
      l8ItwUploadToggle,
      isExecSpouseFlow,
      otherIndividualExecutorEstateTransactions,
      otherIraExecutorEstateTransactions,
      isL8ITWUploadPageVisited,
      isContactInfoConfirmed,
    );
  },
);

export const selectMultiInheritanceEstateNextPage = createSelector(
  getIsReviewPageReachedAndEditMode,
  (isReviewPageReachedAndEditMode: boolean) =>
    getMultiInheritanceEstateNextPage(isReviewPageReachedAndEditMode),
);

export const selectMultiInheritancePersonalInfoNextPage = createSelector(() =>
  getMultiInheritancePersonalInfoNextPage(),
);

export const selectMultiInheritanceContactInfoNextPage = createSelector(
  getIsReviewPageReachedAndEditMode,
  selectIsOAReviewPageReachedAndEditMode,
  selectIsCurrentTransactionFirstNewAccount,
  selectIsEditingProfile,
  selectInheritanceVCDToggle,
  (
    isReviewPageReachedAndEditMode: boolean,
    isOAReviewPageReachedAndEditMode: boolean,
    isCurrentTransactionFirstNewAccount: boolean | undefined,
    isEditingProfile: boolean,
    isVCDEnabled: boolean,
  ) =>
    getMultiInheritanceContactInfoNextPage(
      isReviewPageReachedAndEditMode,
      isOAReviewPageReachedAndEditMode,
      isCurrentTransactionFirstNewAccount ?? false,
      isEditingProfile,
      isVCDEnabled,
    ),
);

export const selectMultiInheritanceEmploymentNextPage = createSelector(
  selectEmploymentMfeDataByOwnerIndex(0),
  getIsReviewPageReachedAndEditMode,
  (
    employmentState: Partial<VgEmploymentState> | undefined,
    isReviewPageReachedAndEditMode: boolean,
  ) => {
    return getMultiInheritanceEmploymentNextPage(employmentState, isReviewPageReachedAndEditMode);
  },
);

export const selectMultiInheritanceLetterUploadNexPage = createSelector(
  getIsReviewPageReachedAndEditMode,
  selectCurrentTransactionHasOnlyTrustee,
  (isReviewPageReachedAndEditMode: boolean, isOnlyTrustee: boolean) =>
    getMultiInheritanceLetterUploadNexPage(isReviewPageReachedAndEditMode, isOnlyTrustee),
);

export const selectMultiInheritanceIncomeNetWorthNextPage = createSelector(
  getIsReviewPageReachedAndEditMode,
  selectIsCurrentTransactionFirstNewAccount,
  (
    isReviewPageReachedAndEditMode: boolean,
    isCurrentTransactionFirstNewAccount: boolean | undefined,
  ) =>
    getMultiInheritanceIncomeNetWorthNextPage(
      isReviewPageReachedAndEditMode,
      isCurrentTransactionFirstNewAccount ?? false,
    ),
);

export const selectMultiInheritanceInvestObjNextPage = createSelector(
  getIsReviewPageReachedAndEditMode,
  (isReviewPageReachedAndEditMode: boolean) =>
    getMultiInheritanceInvestObjNextPage(isReviewPageReachedAndEditMode),
);

export const selectMultiInheritanceNewAccountReviewNextPage = createSelector(
  selectCurrentTransactionId,
  (currentTransactionId) => getMultiInheritanceNewAccountReviewNextPage(currentTransactionId),
);

export const selectMultiInheritanceReviewNextPage = createSelector(
  selectAllTransactions,
  selectInheritanceNewTermsToggle,
  (transactions: TransactionState[], isNewTermsEnabled: boolean) =>
    getMultiInheritanceReviewNextPage(transactions, isNewTermsEnabled),
);

export const selectMultiInheritanceTermsNextPage = createSelector(nullState, () =>
  getMultiInheritanceTermsNextPage(),
);

export const selectMultiInheritanceSettlementFundNextPage = createSelector(
  selectIsMultipleNewAccountTransactionExist,
  (isMultipleNewAccountTransaction: boolean) =>
    getMultiInheritanceSettlementFundNextPage(isMultipleNewAccountTransaction),
);

export const selectMultiInheritanceTrustInfoNextPage = createSelector(() =>
  getMultiInheritanceTrustInfoNextPage(),
);

export const selectMultiInheritanceTrustTaxInfoNextPage = createSelector(
  getIsReviewPageReachedAndEditMode,
  getPersonalDataState,
  selectAllTransactions,
  getIsReviewPageReachedAndSkipInvestmentRevision,
  (
    isReviewPageReachedAndEditMode: boolean,
    personalData: PersonalDataState,
    transactions: false | TransactionState[],
    skipInvestments: boolean,
  ) =>
    getMultiInheritanceTrustTaxInfoNextPage({
      personalData,
      isReviewPageReachedAndEditMode,
      transactions: transactions || [],
      skipInvestments,
    }),
);

export const selectMultiInheritanceL8ItwUploadNextPage = createSelector(
  selectCurrentTransactionType,
  getIsReviewPageReachedAndEditMode,
  selectCurrentTransaction,
  selectCurrentTransactionHasOnlyTrustee,
  selectCurrentTransactionHasOnlyExecutor,
  selectIndividualExecutorEstateTransactions,
  selectIraExecutorEstateTransactions,
  (
    currentTransactionType,
    isReviewPageReachedAndEditMode,
    currentTransaction,
    isOnlyTrustee,
    isOnlyExecutor,
    individualExecutorEstateTransactions,
    iraExecutorEstateTransactions,
  ) => {
    const accountTriageState = currentTransaction?.accountTriage || ({} as AccountTriageState);
    const isIndividualExecutor =
      accountTriageState.accountTypeInheriting === AccountTypes.INDV &&
      accountTriageState.individualDesignation === IndividualDesignationListedTypes.EXECUTOR;
    const isIRASingleExecutor =
      accountTriageState.accountTypeInheriting === AccountTypes.IRA && isOnlyExecutor;
    const isExecSpouseFlow =
      accountTriageState.isDecedentSpouse === YesNoEmpty.YES && isOnlyExecutor;

    const currentId = currentTransaction?.currentTransactionId;
    const otherIndividualExecutorEstateTransactions = individualExecutorEstateTransactions.filter(
      (transaction) => transaction.currentTransactionId !== currentId,
    );
    const otherIraExecutorEstateTransactions = iraExecutorEstateTransactions.filter(
      (transaction) => transaction.currentTransactionId !== currentId,
    );

    return getMultiInheritanceL8ItwUploadNextPage(
      currentTransactionType,
      isReviewPageReachedAndEditMode,
      isIndividualExecutor,
      isIRASingleExecutor,
      isOnlyTrustee,
      isOnlyExecutor,
      otherIndividualExecutorEstateTransactions,
      otherIraExecutorEstateTransactions,
      isExecSpouseFlow,
    );
  },
);
